import {
  Button,
  Input,
  App,
  Progress,
  Tooltip,
  Typography,
  Upload,
  theme,
} from 'antd'
import type { RcFile } from 'antd/es/upload'
import {
  CloseOutlined,
  EditOutlined,
  PaperClipOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { FocusEvent, KeyboardEvent, ReactNode, Ref } from 'react'
import { Functions } from 'firebase/functions'
import { useStorage, useUser } from 'reactfire'
import { api } from '../../lib/api'
import { ChatwootCleoCard } from './ChatwootCleoCard'
import { ChatwootCleoComposeModal } from './ChatwootCleoComposeModal'
import { useChatwootCleoCompose } from './hooks/useChatwootCleoCompose'
import { useChatwootCleoSuggestion } from './hooks/useChatwootCleoSuggestion'
import { MAX_OUTBOUND_ATTACHMENTS } from '../../lib/shared/chatwootAttachments'
import {
  deleteChatwootAttachments,
  stageChatwootAttachments,
  type StagingProgress,
} from './attachmentUploads'
import { useOutboundAttachments } from './useOutboundAttachments'
import { clearPendingAttachments, composerKey } from './composerOutbox'
import { usePendingAttachments } from './usePendingAttachments'
import {
  errorResponse,
  getLocalizedErrorMessage,
} from '../../lib/errorResponse'
import {
  useAttachmentDropzone,
  dropzoneHighlightStyle,
} from './useAttachmentDropzone'
import { AttachmentDropOverlay } from './AttachmentDropOverlay'
import { useChatwootCannedResponses } from './hooks/useChatwootCannedResponses'
import { useChatwootOperatorSignature } from './hooks/useChatwootOperatorSignature'
import { cannedQueryAt, filterCanned, insertCanned } from './cannedResponses'
import { clearDraft, loadDraft, saveDraft } from './composerDrafts'
import { composeReplyWithDraft, hasSignature } from './replySignature'
import {
  filterMentionTargets,
  insertMention,
  mentionQueryAt,
  mentionTargets,
  serializeMentions,
} from './mentions'
import { useChatwootMentionTargets } from './hooks/useChatwootMentionTargets'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import type {
  ChatwootAssignee,
  ChatwootCannedResponsesResponse,
  ChatwootDirectoryResponse,
  ChatwootTeamRef,
} from './types'
import { type ReplyRecipients } from './ChatwootReplyRecipients'
import { type ReplySeed } from './replySeed'
import {
  ChatwootComposerHeader,
  type CleoChipState,
} from './ChatwootComposerHeader'
import { normalizeRecipients } from '../../lib/shared/chatwootRecipients'
import styles from './ChatwootInbox.module.less'

const { TextArea } = Input

// How long the Cleo card plays its exit animation before the composer swaps in
// the reply field. Matches the keyframe durations in ChatwootInbox.module.less.
const CLEO_EXIT_MS = 280

type CleoExitKind = 'accept' | 'copy' | 'reject'

// Marks the body's scrolling element — the Cleo draft box or the textarea — so
// the composer can tell whether its content fits the default height (CL-2886).
const SCROLL_TARGET_SELECTOR = '[data-composer-scroll]'

// The recipient fields as the callable expects them: exactly what the row
// displays, with empty fields omitted. Omitting `to` hands the decision to
// Chatwoot, which addresses the conversation's contact — acceptable only when
// there was never an address to show. A send must never omit recipients merely
// because the operator didn't touch them: the row would then display one
// recipient while Chatwoot delivers to another.
function buildSubmittedRecipients(current: ReplyRecipients): {
  to?: string[]
  cc?: string[]
  bcc?: string[]
} {
  const submitted: { to?: string[]; cc?: string[]; bcc?: string[] } = {}
  for (const field of ['to', 'cc', 'bcc'] as const) {
    const values = normalizeRecipients(current[field])
    if (values.length > 0) {
      submitted[field] = values
    }
  }
  return submitted
}

interface ChatwootComposerProps {
  functions: Functions
  conversationId: string
  companyUid?: string | null
  agents: ChatwootAssignee[]
  teams: ChatwootTeamRef[]
  // Id of the newest incoming message still without a reply (see
  // getAwaitingReplyMessageId), or null when nothing awaits an answer. Gates the
  // Cleo suggestion: Cleo drafts only for unanswered customer messages (Intercom
  // parity), never merely because a conversation is open.
  awaitingReplyMessageId: string | null
  // True while the open thread's messages have not arrived yet. The awaited id
  // reads null then, which says nothing about whether the customer is waiting.
  messagesLoading?: boolean
  // True when the open conversation is an email thread. Only then does the reply
  // carry the operator's signature (chat/widget replies never do).
  isEmailConversation: boolean
  // Where an email reply to this thread should go, derived from the thread
  // itself (see deriveReplySeed). Shown above the field so the operator can see
  // where their answer is going before sending. Undefined on chat threads.
  replySeed?: ReplySeed
  onSent: () => void
}

export function ChatwootComposer({
  functions,
  conversationId,
  companyUid,
  agents,
  teams,
  awaitingReplyMessageId,
  messagesLoading = false,
  isEmailConversation,
  replySeed,
  onSent,
}: ChatwootComposerProps) {
  const { message } = App.useApp()
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const storage = useStorage()
  const { data: user } = useUser()
  const [mode, setMode] = useState<'reply' | 'note'>('reply')
  // The row starts as a statement of where the reply will go rather than as a
  // blank to fill in.
  const initialRecipients = useMemo<ReplyRecipients>(
    () => ({
      to: replySeed?.to ?? [],
      cc: replySeed?.cc ?? [],
      bcc: [],
    }),
    [replySeed]
  )
  const [recipients, setRecipients] =
    useState<ReplyRecipients>(initialRecipients)
  // Which conversation the recipients above belong to, and whether the operator
  // has touched them. Both are needed because `replySeed` can change while
  // staying on the same thread — it is derived from thread data that refetches,
  // so the addresses can resolve late or shift after a send.
  const recipientsForRef = useRef<string | null>(null)
  const recipientsEditedRef = useRef(false)
  useEffect(() => {
    // Switching conversations must not carry one thread's recipients into the
    // next — that would send a reply to a client who was never part of it.
    if (recipientsForRef.current !== conversationId) {
      recipientsForRef.current = conversationId
      recipientsEditedRef.current = false
      setRecipients(initialRecipients)
      return
    }
    // Same conversation, new default: adopt it so the row still shows where the
    // reply is going — but never on top of an edit the operator already made.
    if (!recipientsEditedRef.current) {
      setRecipients(initialRecipients)
    }
  }, [conversationId, initialRecipients])
  // Files selected for the next send. Staged to Storage only on submit, then
  // forwarded to Chatwoot by the callable. The shared picker enforces the same
  // per-file, count AND combined-size caps as the backend. Swapped per
  // conversation+mode by usePendingAttachments below, so attachments never leak
  // across conversations.
  const {
    attachments,
    onSelect: onSelectAttachments,
    remove: removeAttachment,
    replace: replaceAttachments,
  } = useOutboundAttachments()
  // Progress of the in-flight staging upload, or null when nothing is uploading.
  // Drives a determinate bar + cancel button instead of an unbounded spinner.
  const [uploadProgress, setUploadProgress] = useState<StagingProgress | null>(
    null
  )
  const sendAbortRef = useRef<AbortController | null>(null)
  // Seed from any persisted draft for this conversation/mode (REQ-05). The
  // composer instance is reused across conversations, so the effect below
  // reloads the right draft whenever the conversation or mode changes.
  const [content, setContent] = useState(() =>
    loadDraft(conversationId, 'reply')
  )
  const [submitting, setSubmitting] = useState(false)
  // Latest displayed conversation, readable from async callbacks. A send that
  // resolves after the operator switches conversations uses this to avoid
  // resetting the new conversation's composer (see handleSubmit).
  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  // The acting operator's Chatwoot signature (Profile → Message Signature),
  // auto-inserted into email replies so outbound mail carries it — Chatwoot only
  // appends the built-in signature in its own composer, never on the REST reply
  // this UI posts, so we mirror the new-conversation composer here.
  const { signature, status: signatureStatus } =
    useChatwootOperatorSignature(companyUid)
  // Blocks send until the lookup settles, or a fast typist on a slow connection
  // sends unsigned. A failure unblocks rather than stranding the operator.
  const signatureBlocksSend =
    isEmailConversation && mode === 'reply' && signatureStatus === 'loading'
  // Survives the card exit resetting `cleo`; conversation-tagged so a send
  // elsewhere can't inherit it.
  const copiedDraftRef = useRef<{
    conversationId: string
    suggestionId: string
  } | null>(null)

  // Cleo reply-draft lifecycle: the card replaces the reply field while a
  // suggestion is generating or ready; on-demand generation and regeneration
  // flow through here too.
  const cleo = useChatwootCleoSuggestion({
    functions,
    conversationId,
    companyUid,
    awaitingReplyMessageId,
  })
  // Cleo writing a message the advisor starts (CL-2217). Apart from `cleo`
  // above: that follows the customer message awaiting an answer, this is for
  // when nothing is waiting and the advisor says what to write.
  const cleoCompose = useChatwootCleoCompose(functions)
  const [composeOpen, setComposeOpen] = useState(false)
  // The card's exit animation ('accept' lifts up, 'copy' drops down, 'reject'
  // fades) while it hands off to the reply field. Null when no exit is playing.
  const [cleoExit, setCleoExit] = useState<CleoExitKind | null>(null)
  // The operator asked to regenerate the draft on screen. The card keeps the
  // field while the new one is written, so the spinner replaces the old text in
  // place. A background draft, or one asked for with "Generate suggestion", does
  // not: the reply field stays usable until a draft is ready.
  const [cleoRegenerating, setCleoRegenerating] = useState(false)
  const [cleoSending, setCleoSending] = useState(false)
  // A just-copied reply flashes once so the operator sees the draft landed.
  const [replyFlash, setReplyFlash] = useState(false)
  const exitTimerRef = useRef<number | null>(null)
  useEffect(
    () => () => window.clearTimeout(exitTimerRef.current ?? undefined),
    []
  )
  // Carries hand-over text across the mode change; the draft store is
  // best-effort and cannot be written and read back.
  const replyHandoverRef = useRef<{
    conversationId: string
    text: string
  } | null>(null)

  useEffect(() => {
    // Honoured only for its own conversation on the reply tab.
    const handover = replyHandoverRef.current
    replyHandoverRef.current = null
    const seeded =
      handover !== null &&
      handover.conversationId === conversationId &&
      mode === 'reply'
    setContent(seeded ? handover.text : loadDraft(conversationId, mode))
  }, [conversationId, mode])

  // Pending attachments belong to a single conversation + mode. They are also
  // persisted: a file picked but not yet sent survives a reload, a conversation
  // switch, and a failed send.
  const attachmentKey = composerKey(conversationId, mode)
  usePendingAttachments({
    key: attachmentKey,
    attachments,
    replace: replaceAttachments,
  })

  // Abandon any in-flight Cleo exit animation when the conversation changes, or
  // when a new customer message arrives in the open one. The exit timer's
  // closure captured the previous context's draft text and save target, so
  // letting it fire would drop one conversation's AI draft into another's reply
  // box (and persist it under the wrong id). Clearing the timer and the
  // transient card-exit state makes the switched-to context start clean — the
  // same guarantee the old per-message card remount used to give.
  //
  // `awaitingReplyMessageId` belongs in here as much as `conversationId` does: a
  // reply rejected just before the next message lands would otherwise run its
  // cleanup against the NEW message — `cleo.reset()` marks whatever context is
  // live as settled, which silently suppressed that message's own draft, and the
  // trailing caret restore jumped focus to a stale offset in text the operator
  // had since typed.
  useEffect(() => {
    window.clearTimeout(exitTimerRef.current ?? undefined)
    exitTimerRef.current = null
    setCleoExit(null)
    setCleoSending(false)
    setReplyFlash(false)
    setCleoRegenerating(false)
  }, [conversationId, awaitingReplyMessageId])

  // A regeneration is over once the suggestion stops generating (a new draft, or
  // a failure), so the card does not outlive it.
  const cleoGenerating = cleo.status === 'generating'
  useEffect(() => {
    if (!cleoGenerating) {
      setCleoRegenerating(false)
    }
  }, [cleoGenerating])

  // A request typed for one thread must not answer into another: closing the
  // dialog and dropping the run keeps its draft out of the next conversation.
  const resetCleoCompose = cleoCompose.reset
  useEffect(() => {
    setComposeOpen(false)
    resetCleoCompose()
  }, [conversationId, resetCleoCompose])

  // The box grows to fit its content only when the operator asks (CL-2886): a
  // long Cleo draft used to sit in the fixed 200px box with its remainder below
  // the fold, easy to send unread. `bodyOverflows` says whether the content is
  // taller than the box shows, which is when the toggle is worth offering.
  //
  // Expansion is a per-conversation choice: the next thread starts at the
  // default height rather than inheriting a size picked for another draft.
  const boxRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [bodyOverflows, setBodyOverflows] = useState(false)
  // The suggestion the box last opened up for on its own (see below), so a
  // collapse sticks for that draft instead of being undone by the next render.
  const autoExpandedForRef = useRef<string | null>(null)
  useEffect(() => {
    setExpanded(false)
    autoExpandedForRef.current = null
  }, [conversationId])

  const [unseenInboundId, setUnseenInboundId] = useState<string | null>(null)
  const awaitingReplyRef = useRef<{
    conversationId: string
    id: string | null
  } | null>(null)
  useEffect(() => {
    // A thread still loading must not become the baseline: its messages landing
    // a render later would read as a customer message arriving.
    if (messagesLoading) {
      setUnseenInboundId(null)
      return
    }
    const previous = awaitingReplyRef.current
    awaitingReplyRef.current = { conversationId, id: awaitingReplyMessageId }
    if (!previous || previous.conversationId !== conversationId) {
      setUnseenInboundId(null)
      return
    }
    const hasReplyDraft =
      (mode === 'reply' && content.trim().length > 0) ||
      loadDraft(conversationId, 'reply').trim().length > 0
    if (!hasReplyDraft) {
      setUnseenInboundId(null)
      return
    }
    if (
      awaitingReplyMessageId !== null &&
      awaitingReplyMessageId !== previous.id
    ) {
      setUnseenInboundId(awaitingReplyMessageId)
    }
  }, [conversationId, awaitingReplyMessageId, messagesLoading, mode, content])

  // Update + persist together so a draft is never lost on conversation switch
  // or reload. Empty text removes the stored draft.
  function updateContent(next: string) {
    setContent(next)
    saveDraft(conversationId, mode, next)
  }

  // Reach AntD's internal raw <textarea> to move focus/caret after the field
  // (re)mounts. Optional chaining keeps this safe across antd upgrades.
  function focusReplyField(caret?: number) {
    const el = textAreaRef.current?.resizableTextArea?.textArea
    if (!el) {
      return
    }
    el.focus()
    if (caret != null) {
      el.setSelectionRange(caret, caret)
    }
  }

  // A suggestion is always a customer-facing reply, so it lands in the reply
  // field even when the exit finishes on the Note tab.
  function handBackToReplyField(next: string, caret: number) {
    saveDraft(conversationId, 'reply', next)
    replyHandoverRef.current = { conversationId, text: next }
    setMode('reply')
    setContent(next)
    setPendingCaret(caret)
  }

  // The card stays mounted while its exit animation plays; once it finishes we
  // reset the suggestion (unmounting the card) and run `after`, which seeds and
  // focuses the reply field that takes its place. Ordering reset+after together
  // means the reply field is mounted by the time `after` focuses it.
  function beginCleoExit(kind: CleoExitKind, after: () => void) {
    setCleoExit(kind)
    window.clearTimeout(exitTimerRef.current ?? undefined)
    exitTimerRef.current = window.setTimeout(() => {
      cleo.reset()
      setCleoExit(null)
      after()
    }, CLEO_EXIT_MS)
  }

  function recordCleoAction(
    eventType: 'suggestion.copied' | 'suggestion.rejected'
  ) {
    if (!cleo.suggestionId) {
      return
    }
    void api(functions, 'chatwoot/cleo_suggestion_action', {
      conversationId,
      companyUid: companyUid || undefined,
      suggestionId: cleo.suggestionId,
      eventType,
    }).catch(() => undefined)
  }

  // Accept & send: fire the reply and play the lift-up exit. On success the card
  // hands off to an empty reply field: the draft answered the message, so text
  // the operator typed while it was written is dropped too (ENG-6160). On
  // failure the draft is kept so the operator can retry.
  async function onCleoAccept() {
    const text = cleo.suggestion
    if (
      !text ||
      cleoSending ||
      signatureBlocksSend ||
      unseenInboundId !== null
    ) {
      return
    }
    // The manual send button is disabled while a seeded To sits cleared;
    // accept & send must refuse for the same reason, or Chatwoot's contact
    // fallback mails the reply to the very address the operator removed.
    if (
      isEmailConversation &&
      initialRecipients.to.length > 0 &&
      normalizeRecipients(recipients.to).length === 0
    ) {
      message.error(t('frontend:chatwoot.email.recipientRequired'))
      return
    }
    setCleoSending(true)
    setCleoExit('accept')
    const sentConversationId = conversationId
    try {
      await api(functions, 'chatwoot/send_reply', {
        conversationId,
        companyUid: companyUid || undefined,
        content: text,
        ...(isEmailConversation && hasSignature(signature)
          ? { signature: signature as string }
          : {}),
        ...(cleo.suggestionId ? { suggestionId: cleo.suggestionId } : {}),
        ...(isEmailConversation ? buildSubmittedRecipients(recipients) : {}),
      })
      copiedDraftRef.current = null
      clearDraft(sentConversationId, 'reply')
      if (conversationIdRef.current === sentConversationId) {
        setContent('')
      }
      onSent()
      message.success(t('frontend:chatwoot.feedback.replySent'))
      cleo.reset()
      // The draft that needed the room is gone; the empty field doesn't.
      setExpanded(false)
    } catch (_error) {
      message.error(t('frontend:chatwoot.feedback.messageSendFailed'))
    } finally {
      setCleoExit(null)
      setCleoSending(false)
    }
  }

  // Copy to editor: drop the draft into the reply field as an editable draft,
  // then flash and focus it (the card plays its drop-down exit meanwhile). The
  // draft replaces whatever the operator typed while it was written; Reject is
  // the way to keep their own text (ENG-6160).
  function onCleoCopy() {
    const text = cleo.suggestion
    if (!text) {
      return
    }
    recordCleoAction('suggestion.copied')
    // Assign inside the exit callback so a cancelled exit never tags a later send.
    const copiedSuggestionId = cleo.suggestionId
    const next = text
    beginCleoExit('copy', () => {
      copiedDraftRef.current = copiedSuggestionId
        ? { conversationId, suggestionId: copiedSuggestionId }
        : null
      setReplyFlash(true)
      handBackToReplyField(next, next.length)
    })
  }

  // Reject: dismiss the draft and reveal the (draft-seeded) reply field, focused.
  function onCleoReject() {
    recordCleoAction('suggestion.rejected')
    copiedDraftRef.current = null
    // Read at click time, before a tab switch can repoint `content`.
    const replyText = content
    beginCleoExit('reject', () => {
      // The draft that needed the room is gone, and it may have opened the box
      // without being asked; the operator's own text starts at the default.
      setExpanded(false)
      handBackToReplyField(replyText, replyText.length)
    })
  }

  // Canned responses: a "#" trigger in reply mode shows matching prepared
  // replies; selecting one inserts an editable draft (never auto-sent). A fetch
  // failure leaves cannedResponses empty so manual replies are unaffected.
  const { data: cannedData } = useChatwootCannedResponses(companyUid)
  const cannedResponses = useMemo(() => {
    const response = cannedData as ChatwootCannedResponsesResponse | undefined
    return response?.cannedResponses || response?.result?.cannedResponses || []
  }, [cannedData])

  const cannedQuery = mode === 'reply' ? cannedQueryAt(content) : null
  const cannedMatches = useMemo(
    () =>
      cannedQuery === null
        ? []
        : filterCanned(cannedResponses, cannedQuery).slice(0, 6),
    [cannedQuery, cannedResponses]
  )

  function onPickCanned(cannedContent: string) {
    updateContent(insertCanned(content, cannedContent))
  }

  // Mentions: an "@" trigger in note mode suggests agents/teams; selecting one
  // inserts a readable "@Name" token, expanded to Chatwoot mention markup on
  // send. (REQ-01)
  //
  // Scope the picker to who Chatwoot will actually notify in THIS conversation —
  // its inbox members + admins — because Chatwoot silently drops mentions of
  // anyone else. Fall back to the account-wide directory while the scoped list
  // loads or if it comes back empty, so the picker is never needlessly blank.
  const { data: mentionDirectoryData } = useChatwootMentionTargets(
    conversationId,
    companyUid
  )
  const allMentionTargets = useMemo(() => {
    const directory = mentionDirectoryData as
      | ChatwootDirectoryResponse
      | undefined
    const scopedAgents = directory?.agents || directory?.result?.agents || []
    const scopedTeams = directory?.teams || directory?.result?.teams || []
    // Use the scoped result whenever it carried anything (agents OR teams). A
    // scope with teams but no member-agents is still a real scope; only an empty
    // result (placeholder while loading, or a failed call) falls back to the
    // account-wide directory so the picker isn't blank.
    return scopedAgents.length > 0 || scopedTeams.length > 0
      ? mentionTargets(scopedAgents, scopedTeams)
      : mentionTargets(agents, teams)
  }, [mentionDirectoryData, agents, teams])

  // Caret position drives the @-trigger so mentions work mid-note, not only at
  // the end. The ref lets us restore the caret after a pick splices text in.
  const textAreaRef = useRef<TextAreaRef>(null)
  const [caretIndex, setCaretIndex] = useState(0)
  const [pendingCaret, setPendingCaret] = useState<number | null>(null)
  // Which mention suggestion the keyboard has highlighted, so Enter/Tab in the
  // textarea can pick it (matching Intercom) instead of only a mouse click.
  const [activeMentionIndex, setActiveMentionIndex] = useState(0)
  useEffect(() => {
    if (pendingCaret === null) {
      return
    }
    focusReplyField(pendingCaret)
    setPendingCaret(null)
    // focusReplyField reads a ref, so it's stable; only the caret target matters.
  }, [pendingCaret])

  const mentionContext =
    mode === 'note' ? mentionQueryAt(content, caretIndex) : null
  const mentionQuery = mentionContext?.query ?? null
  const mentionMatches = useMemo(
    () =>
      mentionQuery === null
        ? []
        : filterMentionTargets(allMentionTargets, mentionQuery).slice(0, 6),
    [mentionQuery, allMentionTargets]
  )
  useEffect(() => {
    setActiveMentionIndex(0)
  }, [mentionQuery])

  function onPickMention(target: (typeof allMentionTargets)[number]) {
    if (!mentionContext) {
      return
    }
    const next = insertMention(content, mentionContext, target, caretIndex)
    updateContent(next.content)
    setCaretIndex(next.caret)
    setPendingCaret(next.caret)
  }

  async function handleSubmit() {
    // Capture what we're sending: the send is async and the operator may switch
    // conversations before it resolves, so we must not act on the (possibly
    // changed) live state afterwards.
    const submittedConversationId = conversationId
    const submittedMode = mode
    // Exactly what the To row displays, edited or not. Sending only edited
    // fields let an unedited reply fall back to Chatwoot's contact default,
    // which on a forwarded thread mails the forwarding colleague while the row
    // shows the client — the composer must deliver what it displays. Notes are
    // never emailed, so recipients are meaningless there.
    const submittedRecipients =
      submittedMode === 'reply' && isEmailConversation
        ? buildSubmittedRecipients(recipients)
        : {}
    const trimmedContent = content.trim()
    const copiedDraft = copiedDraftRef.current
    const copiedSuggestionId =
      submittedMode === 'reply' &&
      copiedDraft?.conversationId === submittedConversationId
        ? copiedDraft.suggestionId
        : null
    // Only email replies carry the auto-signature, and Cleo's draft never
    // includes it, so scoring a verbatim send reads as a partial rewrite unless
    // the block is removed first.
    const copiedSuggestionSignature =
      copiedSuggestionId && isEmailConversation ? signature : null
    const outboundContent =
      submittedMode === 'note'
        ? serializeMentions(trimmedContent, allMentionTargets)
        : trimmedContent
    const hasMessage = Boolean(trimmedContent)
    if (!hasMessage && attachments.length === 0) {
      return
    }
    if (attachments.length > 0 && !user) {
      message.error(t('frontend:chatwoot.feedback.messageSendFailed'))
      return
    }

    setSubmitting(true)
    const abortController = new AbortController()
    sendAbortRef.current = abortController
    let uploadedPaths: string[] = []
    try {
      // Staging owns the progress bar, so clear it the moment staging settles
      // — either way. An inner `finally` keeps that true by construction: the
      // cancel button is gated on `uploadProgress`, and abort only reaches the
      // staging step, so letting it survive into the callable below would show
      // a cancel that cannot do anything.
      try {
        uploadedPaths = user
          ? await stageChatwootAttachments({
              storage,
              uid: user.uid,
              conversationId: submittedConversationId,
              files: attachments,
              onProgress: setUploadProgress,
              signal: abortController.signal,
            })
          : []
      } finally {
        setUploadProgress(null)
      }
      await api(
        functions,
        submittedMode === 'reply' ? 'chatwoot/send_reply' : 'chatwoot/add_note',
        {
          conversationId: submittedConversationId,
          companyUid: companyUid || undefined,
          content: outboundContent,
          ...(submittedMode === 'reply' &&
          isEmailConversation &&
          hasSignature(signature)
            ? { signature: signature as string }
            : {}),
          ...(copiedSuggestionId ? { copiedSuggestionId } : {}),
          ...(copiedSuggestionSignature ? { copiedSuggestionSignature } : {}),
          ...(uploadedPaths.length > 0 ? { attachments: uploadedPaths } : {}),
          ...submittedRecipients,
        }
      )
      copiedDraftRef.current = null
      clearDraft(submittedConversationId, submittedMode)
      // Chatwoot has the message now, so the recovery copies have done their
      // job. Clearing is keyed to what we submitted, not to what is on screen.
      await clearPendingAttachments(
        composerKey(submittedConversationId, submittedMode)
      )
      onSent()
      // Only reset the live composer if we're still on the conversation we sent
      // to — otherwise we'd wipe the input the operator switched to.
      if (conversationIdRef.current === submittedConversationId) {
        setContent('')
        replaceAttachments([])
        setExpanded(false)
      }
      message.success(
        submittedMode === 'reply'
          ? t('frontend:chatwoot.feedback.replySent')
          : t('frontend:chatwoot.feedback.privateNoteAdded')
      )
    } catch (error) {
      // The callable deletes staged files only on success, so clean up here if
      // the send failed after upload (best-effort; ignore delete errors).
      await deleteChatwootAttachments(storage, uploadedPaths)
      // Deliberately NOT clearing the draft or the attachments: the message the
      // operator wrote is the thing worth protecting.
      if ((error as { name?: string })?.name === 'AbortError') {
        message.info(t('frontend:chatwoot.attachment.sendCanceled'))
      } else {
        errorResponse(
          error,
          getLocalizedErrorMessage(
            error,
            t('frontend:chatwoot.feedback.messageSendFailed')
          ),
          { area: 'chatwoot_send', mode: submittedMode }
        )
      }
    } finally {
      sendAbortRef.current = null
      setSubmitting(false)
    }
  }

  // Abort an in-flight staging upload. The catch above keeps the draft and the
  // attachments, so cancelling is safe — the operator can retry immediately.
  function cancelSend() {
    sendAbortRef.current?.abort(new DOMException('Send canceled', 'AbortError'))
  }

  const isNote = mode === 'note'
  // The Cleo card occupies the reply body once a draft is ready (or mid-exit, or
  // while a regeneration of that draft runs). From then on the operator answers
  // the draft with Send, Copy or Reject; the reply field is back after any of
  // them. Text typed meanwhile is kept under the card: Reject (also after a
  // Regenerate) hands it back, while Copy and Send replace it with the draft.
  //
  // Not while a draft is still being written. Then the reply field stays usable
  // and the header chip says Cleo is working, so a slow run or a Bedrock outage
  // never keeps the operator from answering the customer (ENG-6160).
  const cleoCardShown =
    mode === 'reply' &&
    (cleoExit !== null ||
      cleo.status === 'ready' ||
      (cleo.status === 'generating' && cleoRegenerating))
  // Let operators drop files anywhere on the composer, routed through the same
  // beforeUpload validation as the paperclip button. Disabled while a send is
  // in flight and while the Cleo draft card occupies the reply body — there's
  // no attachment picker mounted then, so a dropped file would have nowhere to
  // land.
  const { isDraggingOver, dropzoneProps } = useAttachmentDropzone({
    onSelect: onSelectAttachments,
    disabled: submitting || cleoCardShown,
  })

  // "Generate suggestion" is offered when a customer message awaits a reply and
  // no card is up — including after a reject or a failed generation.
  const showGenerate =
    mode === 'reply' &&
    awaitingReplyMessageId !== null &&
    cleoExit === null &&
    (cleo.status === 'idle' || cleo.status === 'error')

  // "Write with Cleo" is offered when nothing awaits a reply, so there is no
  // customer message for a draft to answer and the advisor gives the request.
  // Where a message does await, "Generate suggestion" is the way in, and
  // regenerating takes instructions from there.
  const showCompose =
    mode === 'reply' &&
    awaitingReplyMessageId === null &&
    cleoExit === null &&
    !cleoCardShown &&
    cleo.status === 'idle'

  // Re-measure whenever what the body shows can change height: the text, the
  // draft, which body is mounted, the expanded state, and the extras stacked in
  // the field (attachment chips, upload bar, inbound notice). A ResizeObserver on
  // the box covers the rest — window or pane resizes, and the textarea's own
  // autosize settling a frame after the content changed.
  const uploading = uploadProgress !== null
  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) {
      return
    }
    const measure = () => {
      const target = box.querySelector<HTMLElement>(SCROLL_TARGET_SELECTOR)
      // 1px of slack: fractional layout can leave scrollHeight a pixel over
      // clientHeight on content that fits.
      setBodyOverflows(
        target !== null && target.scrollHeight > target.clientHeight + 1
      )
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [
    content,
    cleo.suggestion,
    cleoCardShown,
    mode,
    expanded,
    attachments.length,
    uploading,
    unseenInboundId,
  ])
  // A ready Cleo draft that doesn't fit opens the box by itself, so the whole
  // suggestion is in view from the start rather than behind a click. Once per
  // draft: collapsing it is respected, and a regenerated draft (new id) or the
  // same conversation re-opened opens up again. Typed text never auto-expands —
  // growing under the operator's caret would be the box moving on its own.
  const readyDraftId =
    cleoCardShown && cleo.status === 'ready' && cleoExit === null
      ? (cleo.suggestionId ?? cleo.suggestion)
      : null
  useEffect(() => {
    if (
      readyDraftId !== null &&
      bodyOverflows &&
      autoExpandedForRef.current !== readyDraftId
    ) {
      autoExpandedForRef.current = readyDraftId
      setExpanded(true)
    }
  }, [readyDraftId, bodyOverflows])
  // Offered while the content doesn't fit, and kept while expanded so the
  // operator can always shrink the box back.
  const canToggleExpand = expanded || bodyOverflows

  const boxClassName = [
    styles.composerBox,
    expanded ? styles.composerBoxExpanded : '',
    isNote
      ? styles.composerBoxNote
      : cleoCardShown
        ? styles.composerBoxCleo
        : '',
  ]
    .filter(Boolean)
    .join(' ')
  // Which Cleo state the header chip reports. Ordered by what is on screen
  // first: the card is about the body, and only once it is not up does the
  // suggestion status itself decide. An error and a decline are reported here
  // rather than as a line above the field, which is what used to cost the reply
  // box two of its four lines.
  function determineCleoChip(): CleoChipState | null {
    if (isNote) {
      return null
    }
    if (cleoCardShown) {
      return 'draft'
    }
    if (cleo.status === 'generating') {
      return 'generating'
    }
    if (cleo.status === 'error') {
      return 'error'
    }
    if (cleo.status === 'noReply') {
      return 'noReply'
    }
    if (showGenerate) {
      return 'generate'
    }
    if (showCompose) {
      return 'compose'
    }
    return null
  }
  const cleoChip = determineCleoChip()

  // The chip is status and action in one, so only the actionable states do
  // anything. Asking for a draft does not take the field: the reply field stays
  // usable until the draft is ready.
  function onCleoChipClick() {
    if (cleoChip === 'generate' || cleoChip === 'error') {
      void cleo.generate()
      return
    }
    if (cleoChip === 'compose') {
      setComposeOpen(true)
    }
  }

  // Run Cleo on the advisor's request and drop the result into the reply field
  // as an editable draft — never sent on its own. The dialog stays up while it
  // runs and on failure (keeping the request for a retry), and closes only once
  // there is a draft to show.
  async function onComposeSubmit(instructions: string) {
    const text = await cleoCompose.compose({
      conversationId,
      companyUid,
      instructions,
    })
    if (!text) {
      return
    }
    // Not a Cleo suggestion that was copied: there is no stored draft to
    // measure the send against, so no earlier copy may be tagged onto this one.
    copiedDraftRef.current = null
    setComposeOpen(false)
    const next = composeReplyWithDraft(content, text)
    setReplyFlash(true)
    handBackToReplyField(next, next.length)
  }

  const cleoExitClass =
    cleoExit === 'accept'
      ? styles.cleoCardExitAccept
      : cleoExit === 'copy'
        ? styles.cleoCardExitCopy
        : cleoExit === 'reject'
          ? styles.cleoCardExitReject
          : undefined

  // Sending no to_emails makes Chatwoot fall back to the conversation's
  // contact, so an empty To would deliver the reply to exactly the person the
  // operator removed — the opposite of what clearing it means.
  //
  // Only when there WAS a default to clear. `isEmailConversation` and
  // `replySeed` can resolve a beat apart; requiring a non-empty To in general
  // would refuse sends during that window, and on threads with no address
  // evidence at all.
  const emailNeedsRecipient =
    !isNote && isEmailConversation && initialRecipients.to.length > 0
  const hasRecipient = normalizeRecipients(recipients.to).length > 0

  const canSubmit =
    (content.trim().length > 0 || attachments.length > 0) &&
    !signatureBlocksSend &&
    (!emailNeedsRecipient || hasRecipient) &&
    (isNote || unseenInboundId === null)

  // Show the drop overlay only when a drop can actually be accepted — i.e. the
  // dropzone isn't disabled (no Cleo card up, not mid-send).
  const showDropOverlay = isDraggingOver && !submitting && !cleoCardShown

  return (
    <div
      ref={boxRef}
      className={boxClassName}
      data-testid="chatwoot-composer-dropzone"
      {...dropzoneProps}
      style={{
        position: 'relative',
        ...(showDropOverlay ? dropzoneHighlightStyle(token) : {}),
      }}
    >
      <ChatwootComposerHeader
        mode={mode}
        onModeChange={setMode}
        isEmailConversation={isEmailConversation}
        tone={isNote ? 'note' : cleoCardShown ? 'cleo' : 'reply'}
        recipients={recipients}
        onRecipientsChange={(next) => {
          recipientsEditedRef.current = true
          setRecipients(next)
        }}
        missingRecipient={emailNeedsRecipient && !hasRecipient}
        submitting={submitting}
        companyUid={companyUid}
        cleoChip={cleoChip}
        onCleoChipClick={onCleoChipClick}
        expand={
          canToggleExpand
            ? { expanded, onToggle: () => setExpanded((value) => !value) }
            : null
        }
      />

      {mode === 'reply' && unseenInboundId !== null && (
        <div className={styles.composerInboundNotice} role="status">
          <Typography.Text>
            {t('frontend:chatwoot.composer.newInboundNotice')}
          </Typography.Text>
          <Button size="small" onClick={() => setUnseenInboundId(null)}>
            {t('frontend:chatwoot.composer.newInboundAcknowledge')}
          </Button>
        </div>
      )}

      {mode === 'note' ? (
        <ComposerField
          key="note"
          // The reply and note fields are never mounted at once, so the shared
          // ref binds to whichever is showing — the note field needs it too, for
          // @-mention caret restoration after a pick.
          textAreaRef={textAreaRef}
          value={content}
          onChange={(next, caret) => {
            updateContent(next)
            setCaretIndex(caret)
          }}
          onSelectCaret={(caret) => setCaretIndex(caret)}
          onKeyDown={(event) => {
            if (mentionMatches.length === 0) {
              return
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setActiveMentionIndex((i) => (i + 1) % mentionMatches.length)
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              setActiveMentionIndex(
                (i) => (i - 1 + mentionMatches.length) % mentionMatches.length
              )
            } else if (event.key === 'Enter' || event.key === 'Tab') {
              const target =
                mentionMatches[activeMentionIndex] ?? mentionMatches[0]
              if (target) {
                event.preventDefault()
                onPickMention(target)
              }
            }
          }}
          placeholder={t('frontend:chatwoot.action.writePrivateNote')}
          overlay={
            mentionMatches.length > 0 ? (
              <div className={styles.composerSuggestions}>
                {mentionMatches.map((target, index) => (
                  <button
                    key={`${target.type}:${target.id}`}
                    type="button"
                    className={styles.composerSuggestion}
                    data-active={index === activeMentionIndex}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      onPickMention(target)
                    }}
                    onMouseEnter={() => setActiveMentionIndex(index)}
                  >
                    <Typography.Text strong>@{target.name}</Typography.Text>{' '}
                    <Typography.Text type="secondary">
                      {target.type === 'team'
                        ? t('frontend:chatwoot.field.team')
                        : t('frontend:chatwoot.field.agent')}
                    </Typography.Text>
                  </button>
                ))}
              </div>
            ) : null
          }
          attachments={attachments}
          onSelectAttachments={onSelectAttachments}
          onRemoveAttachment={removeAttachment}
          submitting={submitting}
          uploadProgress={uploadProgress}
          onCancelSend={cancelSend}
          expanded={expanded}
          submitDisabled={!canSubmit}
          submitLabel={t('frontend:chatwoot.action.addNote')}
          submitIcon={<EditOutlined />}
          onSubmit={handleSubmit}
          t={t}
        />
      ) : cleoCardShown ? (
        <ChatwootCleoCard
          status={cleo.status === 'ready' ? 'ready' : 'generating'}
          suggestion={cleo.suggestion}
          sending={cleoSending}
          exiting={cleoExit !== null}
          exitClass={cleoExitClass}
          onAccept={() => void onCleoAccept()}
          onCopy={onCleoCopy}
          onReject={onCleoReject}
          onRegenerate={(instructions) => {
            setCleoRegenerating(true)
            void cleo.regenerate(instructions)
          }}
        />
      ) : (
        <>
          {/* Recipients and Cleo's status both live in the header row now, so
              nothing stands between this field and the box. Where the reply
              lands still matters — Chatwoot addresses it to the conversation's
              contact, and a thread started from a forwarded mail has the
              forwarder as its contact — which is why the header shows the
              address and opens the editor over the composer. */}
          <ComposerField
            key="reply"
            textAreaRef={textAreaRef}
            value={content}
            flash={replyFlash}
            onFlashEnd={() => setReplyFlash(false)}
            onChange={(next, caret) => {
              updateContent(next)
              setCaretIndex(caret)
            }}
            onSelectCaret={(caret) => setCaretIndex(caret)}
            placeholder={t('frontend:chatwoot.action.writeCustomerReply')}
            overlay={
              cannedMatches.length > 0 ? (
                <div className={styles.composerSuggestions}>
                  {cannedMatches.map((canned) => (
                    <button
                      key={canned.id}
                      type="button"
                      className={styles.composerSuggestion}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        onPickCanned(canned.content)
                      }}
                    >
                      {canned.shortCode && (
                        <Typography.Text strong>
                          #{canned.shortCode}{' '}
                        </Typography.Text>
                      )}
                      <Typography.Text type="secondary" ellipsis>
                        {canned.content}
                      </Typography.Text>
                    </button>
                  ))}
                </div>
              ) : null
            }
            attachments={attachments}
            onSelectAttachments={onSelectAttachments}
            onRemoveAttachment={removeAttachment}
            submitting={submitting}
            uploadProgress={uploadProgress}
            onCancelSend={cancelSend}
            expanded={expanded}
            submitDisabled={!canSubmit}
            submitLabel={t('frontend:chatwoot.action.sendReply')}
            submitIcon={<SendOutlined />}
            onSubmit={handleSubmit}
            signatureHint={
              isEmailConversation
                ? signatureStatus === 'loading'
                  ? t('frontend:chatwoot.hint.signatureLoading')
                  : signatureStatus === 'failed'
                    ? t('frontend:chatwoot.hint.signatureUnavailable')
                    : hasSignature(signature)
                      ? t('frontend:chatwoot.hint.signatureAppended')
                      : t('frontend:chatwoot.hint.signatureMissing')
                : null
            }
            t={t}
          />
        </>
      )}

      <ChatwootCleoComposeModal
        open={composeOpen}
        generating={cleoCompose.status === 'generating'}
        failed={cleoCompose.status === 'error'}
        onSubmit={(instructions) => void onComposeSubmit(instructions)}
        onCancel={() => {
          cleoCompose.reset()
          setComposeOpen(false)
        }}
      />

      {showDropOverlay && <AttachmentDropOverlay />}
    </div>
  )
}

// The reply or note editing surface: textarea filling the box body, an optional
// suggestion overlay (canned / mentions), the attachment chips, and the bottom
// action bar. Shared by both tabs so the send/add-note controls stay identical.
function ComposerField({
  textAreaRef,
  value,
  onChange,
  onSelectCaret,
  onKeyDown,
  onFocus,
  placeholder,
  overlay,
  flash,
  onFlashEnd,
  attachments,
  onSelectAttachments,
  onRemoveAttachment,
  submitting,
  uploadProgress,
  onCancelSend,
  expanded = false,
  submitDisabled,
  submitLabel,
  submitIcon,
  onSubmit,
  signatureHint,
  t,
}: {
  textAreaRef?: Ref<TextAreaRef>
  value: string
  onChange: (next: string, caret: number) => void
  onSelectCaret: (caret: number) => void
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  onFocus?: (event: FocusEvent<HTMLTextAreaElement>) => void
  placeholder: string
  overlay?: ReactNode
  flash?: boolean
  onFlashEnd?: () => void
  attachments: RcFile[]
  onSelectAttachments: (file: RcFile, batch: RcFile[]) => boolean
  onRemoveAttachment: (file: RcFile) => void
  submitting: boolean
  uploadProgress?: StagingProgress | null
  onCancelSend?: () => void
  // The box is expanded (CL-2886): size the textarea to its text so the box can
  // grow with it, up to the box's own cap.
  expanded?: boolean
  submitDisabled: boolean
  submitLabel: string
  submitIcon: ReactNode
  onSubmit: () => void
  signatureHint?: string | null
  t: ReturnType<typeof useTranslation>['t']
}) {
  const { token } = theme.useToken()
  return (
    <div className={styles.composerField}>
      <TextArea
        ref={textAreaRef}
        value={value}
        onChange={(event) =>
          onChange(
            event.target.value,
            event.target.selectionStart ?? event.target.value.length
          )
        }
        onSelect={(event) =>
          onSelectCaret(event.currentTarget.selectionStart ?? 0)
        }
        // Cmd/Ctrl+Enter sends; Enter and Shift+Enter start a new line, matching
        // Intercom's email composer, so a bare Enter can no longer fire a mail
        // mid-paragraph. Only a modified Enter with Ctrl or Meta sends. The
        // guards below are cases where even that must not send, because a send
        // would destroy work or duplicate a message:
        //
        //   • the caller's own handler claimed it: the @-mention picker uses
        //     Enter to choose a target, and calls preventDefault when it does
        //   • a suggestion list is open at all (mention or canned response);
        //     canned responses are mouse-pick only, so a send there would post a
        //     half-typed "/shortcut" instead of expanding it
        //   • an IME composition is being confirmed, where Enter commits the
        //     candidate rather than ending the line
        //   • the send button is disabled (empty body, no attachments)
        //   • a send is already in flight. `submitDisabled` tracks only whether
        //     there is something to send, and `content` stays populated until
        //     the send succeeds, so it stays false for the whole staging plus
        //     request round trip. The button is covered by antd's `loading`,
        //     which swallows clicks; the keyboard has no such thing, so a second
        //     Cmd/Ctrl+Enter would start a concurrent handleSubmit: duplicate
        //     reply, attachments uploaded twice, and the first send's abort
        //     handle overwritten so it could no longer be cancelled.
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (event.defaultPrevented || event.key !== 'Enter') {
            return
          }
          if (!event.ctrlKey && !event.metaKey) {
            return
          }
          if (
            // `isComposing` is on the native event only; React does not lift it.
            (event.nativeEvent as globalThis.KeyboardEvent).isComposing ||
            overlay != null ||
            submitDisabled ||
            submitting
          ) {
            return
          }
          event.preventDefault()
          onSubmit()
        }}
        onFocus={onFocus}
        variant="borderless"
        placeholder={placeholder}
        data-composer-scroll=""
        // Expanded, the textarea takes its text's height (no maxRows, so it
        // keeps its scrollbar) and the box grows with it until its max-height,
        // where flex shrinks the field back and it scrolls. Collapsed, autosize
        // is off and the field simply fills the fixed-height box.
        autoSize={expanded ? { minRows: 2 } : false}
        // Fill the body; the box owns the height and scrolls the textarea.
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          resize: 'none',
          paddingInline: 14,
          paddingBlock: 12,
          background: 'transparent',
        }}
        className={flash ? styles.composerFieldFlash : undefined}
        onAnimationEnd={onFlashEnd}
      />
      {overlay}
      {attachments.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            padding: '0 12px 4px',
          }}
        >
          {attachments.map((file) => (
            <span
              key={`${file.uid}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: 6,
                padding: '2px 6px',
                maxWidth: 220,
              }}
            >
              <PaperClipOutlined />
              <Typography.Text ellipsis style={{ maxWidth: 150 }}>
                {file.name}
              </Typography.Text>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                aria-label={t('frontend:chatwoot.attachment.remove')}
                onClick={() => onRemoveAttachment(file)}
              />
            </span>
          ))}
        </div>
      )}
      {uploadProgress && uploadProgress.totalBytes > 0 && (
        // A determinate bar, so a slow-but-alive upload is visibly different
        // from a dead one — and a way out that doesn't cost the operator their
        // message.
        <div style={{ padding: '0 12px 4px' }}>
          <Progress
            percent={Math.round(
              (uploadProgress.bytesTransferred / uploadProgress.totalBytes) *
                100
            )}
            size="small"
            status="active"
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('frontend:chatwoot.attachment.uploading', {
              percent: Math.round(
                (uploadProgress.bytesTransferred / uploadProgress.totalBytes) *
                  100
              ),
            })}
          </Typography.Text>
        </div>
      )}
      <div className={styles.composerFieldActions}>
        {signatureHint && (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, marginRight: 'auto' }}
          >
            {signatureHint}
          </Typography.Text>
        )}
        <Upload
          multiple
          showUploadList={false}
          fileList={attachments}
          beforeUpload={onSelectAttachments}
        >
          <Button
            type="text"
            icon={<PaperClipOutlined />}
            disabled={
              submitting || attachments.length >= MAX_OUTBOUND_ATTACHMENTS
            }
          >
            {t('frontend:chatwoot.attachment.attach')}
          </Button>
        </Upload>
        {/* Only while an upload is actually in flight: abort reaches the staging
            step alone, so offering it during an attachment-less send (or after
            staging finished and the callable is running) would promise a cancel
            that cannot happen. */}
        {uploadProgress && onCancelSend && (
          <Button type="text" onClick={onCancelSend}>
            {t('frontend:chatwoot.attachment.cancelUpload')}
          </Button>
        )}
        {/* The keyboard shortcut is only discoverable if we say so somewhere;
            the button is where the operator already looks to send. */}
        <Tooltip title={t('frontend:chatwoot.action.sendHint')}>
          <Button
            type="primary"
            icon={submitIcon}
            loading={submitting}
            disabled={submitDisabled}
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}
